import React, { useState } from "react";
import { Save, Download, Upload, Mail } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../ImageUpload";
import { useSiteSettings, Field, SectionTitle } from "./shared";

const SiteSettingsAdmin = () => {
  const { settings, setSettings, save, saving } = useSiteSettings();
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailAddr, setTestEmailAddr] = useState("");

  if (!settings) return <div className="text-stone-500 p-8">Chargement...</div>;

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/content/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      a.download = `elan-danse-content-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Contenu exporté");
    } catch {
      toast.error("Erreur lors de l'export");
    } finally { setExporting(false); }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!window.confirm("Importer ce fichier va REMPLACER l'intégralité du contenu du site. Les données CRM ne sont PAS touchées. Continuer ?")) return;
    setImporting(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/admin/content/import", payload);
      const total = Object.values(data.counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Import réussi : ${total} éléments restaurés. Rechargement…`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error("Erreur lors de l'import : " + (e?.response?.data?.detail || e.message));
    } finally { setImporting(false); }
  };

  return (
    <div>
      <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Paramètres du site</h1>
      <p className="text-stone-500 mt-1 text-sm sm:text-base">Design, contenus éditoriaux, coordonnées et documents publics. Les réglages financiers sont dans <em>Gestion → Paramètres financiers</em>.</p>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field settings={settings} setSettings={setSettings} fieldKey="name" label="Nom de l'école" />
          <Field settings={settings} setSettings={setSettings} fieldKey="tagline" label="Slogan / sous-titre" />
          <Field settings={settings} setSettings={setSettings} fieldKey="city" label="Ville" />
          <Field settings={settings} setSettings={setSettings} fieldKey="postal" label="Code postal" />
          <Field settings={settings} setSettings={setSettings} fieldKey="address" label="Adresse complète" full />
          <Field settings={settings} setSettings={setSettings} fieldKey="phone" label="Téléphone" />
          <Field settings={settings} setSettings={setSettings} fieldKey="email" label="Email de contact" />

          <div className="md:col-span-2 border border-stone-200 rounded-xl p-3.5 bg-stone-50/60">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!settings.shopEnabled}
                onChange={(e) => setSettings({ ...settings, shopEnabled: e.target.checked })}
                data-testid="settings-shop-enabled"
                className="mt-1 rounded"
              />
              <span>
                <span className="text-sm text-stone-800 font-medium">Afficher la boutique sur le site</span>
                <span className="block text-xs text-stone-500 mt-0.5">
                  Décoché, la boutique est totalement masquée (lien du menu, panier et pages produits) et aucun achat n&apos;est possible.
                  Le back-office Boutique reste accessible pour préparer vos produits tranquillement.
                </span>
              </span>
            </label>
          </div>

          <div className="md:col-span-2">
            <ImageUpload value={settings.logo} onChange={(v) => setSettings({...settings, logo: v})} label="Logo officiel" placeholder="URL du logo" />
          </div>

          <SectionTitle title="Section « Hero » (haut de la page d'accueil)" subtitle="Textes affichés par-dessus l'image hero. Laisser vide pour utiliser les valeurs par défaut." />
          <Field settings={settings} setSettings={setSettings} fieldKey="heroTagline" label="Slogan hero (petit texte manuscrit)" placeholder="École de danse au cœur du Perray-en-Yvelines" full />
          <Field settings={settings} setSettings={setSettings} fieldKey="heroTitle" label="Titre hero (grand titre calligraphié)" placeholder="L'Élan Danse" />
          <Field settings={settings} setSettings={setSettings} fieldKey="heroCtaLabel" label="Libellé du bouton d'appel à l'action" placeholder="S'inscrire" />
          <Field settings={settings} setSettings={setSettings} fieldKey="heroSubline" label="Ligne du dessous (majuscules espacées)" placeholder="Saison 2026/2027 · Le Perray-en-Yvelines" full />
          <div className="md:col-span-2">
            <ImageUpload value={settings.heroImage} onChange={(v) => setSettings({...settings, heroImage: v})} label="Image hero (page d'accueil)" placeholder="URL de l'image hero" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">
              Position verticale de l&apos;image ({settings.heroImagePosition !== undefined && settings.heroImagePosition !== "" ? `${settings.heroImagePosition}%` : "50%"})
            </label>
            <div className="flex items-center gap-3">
              <span className="text-xs text-stone-500 whitespace-nowrap">Haut (0%)</span>
              <input
                type="range" min="0" max="100" step="1"
                value={settings.heroImagePosition !== undefined && settings.heroImagePosition !== "" ? Number(settings.heroImagePosition) : 50}
                onChange={(e) => setSettings({ ...settings, heroImagePosition: e.target.value })}
                data-testid="settings-hero-position"
                className="flex-1 accent-deep-rose"
              />
              <span className="text-xs text-stone-500 whitespace-nowrap">Bas (100%)</span>
              <input
                type="number" min="0" max="100"
                value={settings.heroImagePosition !== undefined && settings.heroImagePosition !== "" ? settings.heroImagePosition : 50}
                onChange={(e) => setSettings({ ...settings, heroImagePosition: e.target.value })}
                className="w-16 bg-stone-50 border border-stone-200 rounded-lg px-2 py-1 text-sm text-center"
              />
              <button
                type="button"
                onClick={() => setSettings({ ...settings, heroImagePosition: "50" })}
                className="text-xs bg-stone-100 hover:bg-stone-200 text-stone-700 px-3 py-1.5 rounded-full whitespace-nowrap"
              >Reset</button>
            </div>
            <p className="text-xs text-stone-500 mt-1.5">Ajustez pour cadrer votre image : 0 = tout en haut · 50 = centre · 100 = tout en bas.</p>
          </div>

          <SectionTitle title="Bloc « L'école » (bas de la page d'accueil)" subtitle="Bloc de présentation avec image de studio, texte descriptif et informations." />
          <Field settings={settings} setSettings={setSettings} fieldKey="schoolBlockKicker" label="Petit titre manuscrit" placeholder="L'école" />
          <Field settings={settings} setSettings={setSettings} fieldKey="schoolBlockTitle" label="Titre principal" placeholder="Au cœur du Perray-en-Yvelines" />
          <Field settings={settings} setSettings={setSettings} fieldKey="schoolBlockText" label="Paragraphe descriptif" type="textarea" placeholder="Installée dans un studio lumineux et chaleureux..." full />
          <Field settings={settings} setSettings={setSettings} fieldKey="schoolBlockScheduleText" label="Ligne « Saison / cours »" placeholder="Saison 2026/2027 — cours du lundi au samedi" full />
          <Field settings={settings} setSettings={setSettings} fieldKey="schoolBlockImageCaption" label="Légende sous la photo (manuscrit)" placeholder="Notre studio" />
          <div />
          <div className="md:col-span-2">
            <ImageUpload value={settings.schoolBlockImage} onChange={(v) => setSettings({...settings, schoolBlockImage: v})} label="Image du studio (bloc L'école)" placeholder="URL de la photo du studio" />
          </div>

          <SectionTitle title="Planning" />
          <Field settings={settings} setSettings={setSettings} fieldKey="planningNote" label="Note affichée sous le planning" type="textarea" full />

          <SectionTitle title="Documents légaux (checkout)" subtitle="URLs des documents affichés dans les cases à cocher de l'inscription. Laissez vide pour utiliser les documents par défaut inclus dans le site." />
          <Field settings={settings} setSettings={setSettings} fieldKey="legalCgvUrl" label="URL — Conditions générales de vente" placeholder="/legal/cgv.pdf" full />
          <Field settings={settings} setSettings={setSettings} fieldKey="legalReglementUrl" label="URL — Règlement intérieur" placeholder="/legal/reglement-interieur.pdf" full />
          <Field settings={settings} setSettings={setSettings} fieldKey="legalImageUrl" label="URL — Droit à l'image / RGPD" placeholder="/legal/annexe-rgpd.pdf" full />
        </div>

        <div className="mt-6">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-coral text-white px-6 py-2.5 rounded-full text-sm font-medium disabled:opacity-50" data-testid="site-settings-save-btn">
            <Save size={16}/> {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mt-6 border-l-4 border-amber-400">
        <h3 className="font-serif-display text-lg text-stone-800 mb-1 flex items-center gap-2"><Mail size={20} className="text-amber-700"/> Notifications email</h3>
        <p className="text-stone-500 text-sm mb-4">
          Les emails sont envoyés automatiquement via Gmail SMTP (compte <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">elanecoledanse@gmail.com</code>).
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="email"
            placeholder="Email pour tester (optionnel — sinon envoyé à l'école)"
            value={testEmailAddr}
            onChange={(e) => setTestEmailAddr(e.target.value)}
            className="flex-1 min-w-[260px] bg-stone-50 border border-stone-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blush"
            data-testid="test-email-input"
          />
          <button
            onClick={async () => {
              setTestingEmail(true);
              try {
                const { data } = await api.post("/admin/test-email", { to: testEmailAddr.trim() || undefined });
                toast.success(`Email de test envoyé à ${data.to}`);
              } catch (e) {
                toast.error("Échec de l'envoi : " + (e?.response?.data?.detail || e.message));
              } finally { setTestingEmail(false); }
            }}
            disabled={testingEmail}
            className="inline-flex items-center gap-2 bg-amber-600 text-white px-5 py-2.5 rounded-full text-sm font-medium disabled:opacity-50"
            data-testid="test-email-btn"
          >
            <Mail size={16}/> {testingEmail ? "Envoi…" : "Tester l'envoi"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mt-6 border-l-4 border-emerald-400">
        <h3 className="font-serif-display text-lg text-stone-800 mb-1">Synchroniser le contenu entre environnements</h3>
        <p className="text-stone-500 text-sm mb-4">
          Exportez l&apos;intégralité du contenu (cours, profs, planning, tarifs, tenues, dates, galerie, infos, catégories, paramètres) et importez-le dans un autre environnement. Les données CRM ne sont jamais affectées.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={handleExport} disabled={exporting} className="inline-flex items-center gap-2 bg-stone-800 text-white px-5 py-2.5 rounded-full text-sm font-medium disabled:opacity-50" data-testid="content-export-btn">
            <Download size={16}/> {exporting ? "Export en cours…" : "Exporter le contenu (.json)"}
          </button>
          <label className="inline-flex items-center gap-2 bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-medium cursor-pointer disabled:opacity-50" data-testid="content-import-btn">
            <Upload size={16}/> {importing ? "Import en cours…" : "Importer un fichier .json"}
            <input type="file" accept="application/json,.json" onChange={handleImport} disabled={importing} className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
};

export default SiteSettingsAdmin;
